import { useState, useRef, useEffect } from "react";
import { api, API } from "@/lib/api";
import { HomeSlideshow, useHomeFoto } from "@/components/HomeSlideshow";
import { useAuth, apiError } from "@/context/AuthContext";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, Globe, UserPlus, Lock, ArrowLeft, Handshake, Presentation, X } from "lucide-react";

const PRESENTAZIONE_URL = "https://share.gemini.google/S9jNza2h6tDC";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(() => searchParams.get("err") || "");
  const [loading, setLoading] = useState(false);
  const [showLogin, setShowLogin] = useState(() => Boolean(searchParams.get("err")));
  const [mode, setMode] = useState("login");
  const [nome, setNome] = useState("");
  const [cognome, setCognome] = useState("");
  const [cf, setCf] = useState("");
  const [presWin, setPresWin] = useState(false);
  const winRef = useRef(null);
  const [pdf, setPdf] = useState(null);
  const homeFoto = useHomeFoto();

  useEffect(() => {
    api.get("/public/presentazione").then((r) => { if (r.data?.available) { setPdf(r.data); } }).catch(() => {});
  }, []);

  const pdfUrl = pdf ? `${API}/public/presentazione/file?v=${encodeURIComponent(pdf.uploaded_at)}` : null;

  const openPresentazione = () => {
    if (pdf) {
      if (window.matchMedia("(min-width: 1024px)").matches) { setPresWin(true); } else { window.open(pdfUrl, "_blank", "noopener"); }
      return;
    }
    const w = Math.round(window.screen.availWidth / 2);
    const h = window.screen.availHeight;
    winRef.current = window.open(PRESENTAZIONE_URL, "presentazione-corso", `left=0,top=0,width=${w},height=${h}`);
    setPresWin(true);
  };

  const closePresentazione = () => {
    try { winRef.current?.close(); } catch (e) { /* finestra non controllabile */ }
    winRef.current = null;
    setPresWin(false);
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register({ nome, cognome, codice_fiscale: cf.trim().toUpperCase(), email, password });
      navigate("/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-2">
      <div className="relative hidden flex-col justify-between overflow-hidden bg-[#14382C] p-12 lg:flex">
        <div className="relative z-10 flex items-center gap-4">
          <img src="/emblem.png" alt="Emblema associazione" className="h-24 w-auto object-contain" />
          <span className="font-heading text-xl font-extrabold leading-tight text-white">Associazione Nazionale Guardie Ecozoofile ODV</span>
        </div>
        <HomeSlideshow foto={homeFoto} className="relative z-10 my-8 aspect-[4/3] w-full max-w-xl" />
        <div className="relative z-10 max-w-md">
          <h1 className="font-heading text-4xl font-extrabold leading-tight text-white">
            Gestione operativa delle guardie zoofile
          </h1>
          <p className="mt-4 text-base text-emerald-100/80">
            Anagrafica, verbali, turni e avvisi scadenze in un'unica piattaforma, sincronizzata e
            pronta sul campo.
          </p>
          <div className="mt-8 flex items-center gap-2 text-sm text-emerald-100/70">
            <ShieldCheck className="h-4 w-4" /> Accesso riservato agli operatori autorizzati
          </div>
        </div>
        {presWin && pdf && (
          <div data-testid="presentazione-panel" className="absolute inset-0 z-20 flex flex-col bg-[#14382C] p-6 gz-fade-up">
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="font-heading text-lg font-bold text-white">Presentazione Corso di Formazione d'ingresso</p>
              <Button type="button" data-testid="presentazione-close-button" onClick={closePresentazione} className="bg-[#D97706] text-white hover:bg-[#b45309]"><X className="mr-2 h-4 w-4" /> Chiudi presentazione</Button>
            </div>
            <iframe data-testid="presentazione-pdf-frame" title="Presentazione Corso di Formazione d'ingresso" src={pdfUrl} className="w-full flex-1 rounded-md border-0 bg-white" />
          </div>
        )}
        {presWin && !pdf && (
          <div data-testid="presentazione-panel" className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-6 bg-[#14382C]/95 p-12 text-center gz-fade-up">
            <Presentation className="h-14 w-14 text-emerald-200" />
            <div>
              <p className="font-heading text-2xl font-bold text-white">Presentazione Corso di Formazione d'ingresso</p>
              <p className="mt-2 text-sm text-emerald-100/80">La presentazione è aperta in una finestra sul lato sinistro. Al termine chiudi la finestra e premi "Chiudi presentazione".</p>
            </div>
            <div className="flex gap-3">
              <Button type="button" data-testid="presentazione-focus-button" variant="outline" onClick={openPresentazione} className="border-emerald-200 bg-transparent text-white hover:bg-emerald-900">Mostra finestra</Button>
              <Button type="button" data-testid="presentazione-close-button" onClick={closePresentazione} className="bg-[#D97706] text-white hover:bg-[#b45309]"><X className="mr-2 h-4 w-4" /> Chiudi presentazione</Button>
            </div>
          </div>
        )}
        <div className="pointer-events-none absolute -right-20 -top-20 h-80 w-80 rounded-full bg-[#166534]/40 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-10 h-72 w-72 rounded-full bg-[#D97706]/20 blur-3xl" />
      </div>

      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 lg:min-h-full">
        <div className="w-full max-w-sm gz-fade-up">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <img src="/emblem.png" alt="Emblema associazione" className="h-20 w-auto object-contain" />
            <span className="font-heading text-lg font-bold leading-tight text-slate-900">Associazione Nazionale Guardie Ecozoofile ODV</span>
          </div>
          {!showLogin && (
            <div className="gz-fade-up">
              <h2 className="font-heading text-3xl font-bold text-slate-900">Guardie Zoofile</h2>
              <p className="mt-2 text-base text-slate-500">Portale dell'Associazione Nazionale Guardie Ecozoofile ODV.</p>
              <HomeSlideshow foto={homeFoto} tid="home-slideshow-mobile" className="mt-6 aspect-[4/3] w-full lg:hidden" />
              <Button
                type="button"
                data-testid="open-login-button"
                onClick={() => setShowLogin(true)}
                className="mt-8 h-auto w-full bg-[#14382C] py-4 text-base hover:bg-[#0E281F]"
              >
                <Lock className="mr-2 h-5 w-5" /> Area riservata Guardie Zoofile
              </Button>
              <p className="mt-3 text-center text-xs text-slate-400">Accesso riservato agli operatori autorizzati</p>
            </div>
          )}

          {showLogin && (
          <div className="gz-fade-up">
          <button
            type="button"
            data-testid="back-to-landing"
            onClick={() => { setShowLogin(false); setError(""); setMode("login"); }}
            className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 transition-colors hover:text-[#166534]"
          >
            <ArrowLeft className="h-4 w-4" /> Indietro
          </button>
          <h2 className="font-heading text-2xl font-bold text-slate-900">{mode === "register" ? "Registrazione tesserati" : "Area riservata"}</h2>
          <p className="mt-1 text-sm text-slate-500">{mode === "register" ? "Inserisci i tuoi dati come in anagrafica: Codice Fiscale, nome e cognome." : "Inserisci le tue credenziali per continuare."}</p>

          {mode === "register" ? (
            <form onSubmit={submitRegister} className="mt-8 space-y-4" data-testid="register-form">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label htmlFor="nome">Nome</Label><Input id="nome" data-testid="register-nome-input" value={nome} onChange={(e) => setNome(e.target.value)} required /></div>
                <div className="space-y-2"><Label htmlFor="cognome">Cognome</Label><Input id="cognome" data-testid="register-cognome-input" value={cognome} onChange={(e) => setCognome(e.target.value)} required /></div>
              </div>
              <div className="space-y-2"><Label htmlFor="cf">Codice Fiscale</Label><Input id="cf" data-testid="register-cf-input" value={cf} onChange={(e) => setCf(e.target.value)} placeholder="RSSMRA80A01H501U" className="font-mono-code uppercase" required /></div>
              <div className="space-y-2"><Label htmlFor="reg-email">Email</Label><Input id="reg-email" type="email" data-testid="register-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@esempio.it" required /></div>
              <div className="space-y-2"><Label htmlFor="reg-password">Password</Label><Input id="reg-password" type="password" data-testid="register-password-input" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Almeno 8 caratteri" required /></div>
              {error && <p data-testid="register-error" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
              <Button type="submit" data-testid="register-submit-button" disabled={loading} className="w-full bg-[#14382C] hover:bg-[#0E281F]">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Registrati</Button>
              <p className="text-center text-xs text-slate-400">Accesso riservato ai tesserati: i dati devono corrispondere all'anagrafica. Dopo la registrazione un coordinatore approva il primo accesso.</p>
              <div className="text-center"><button type="button" data-testid="switch-to-login" onClick={() => { setMode("login"); setError(""); }} className="text-sm text-[#166534] hover:underline">Hai già un account? Accedi</button></div>
            </form>
          ) : (
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@esempio.it"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
            {error && (
              <p data-testid="login-error" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </p>
            )}
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full bg-[#14382C] hover:bg-[#0E281F]"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Accedi
            </Button>

            <div className="relative my-1">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-slate-50 px-2 text-slate-400">oppure</span></div>
            </div>
            <Button
              type="button"
              variant="outline"
              data-testid="google-login-button"
              onClick={googleLogin}
              className="w-full border-slate-300"
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 4.5 29.5 2.5 24 2.5 12.1 2.5 2.5 12.1 2.5 24S12.1 45.5 24 45.5 45.5 35.9 45.5 24c0-1.2-.1-2.3-.4-3.5z"/>
                <path fill="#FF3D00" d="M5.3 14.7l6.6 4.8C13.7 15.1 18.4 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 4.5 29.5 2.5 24 2.5 16.3 2.5 9.6 6.9 5.3 14.7z"/>
                <path fill="#4CAF50" d="M24 45.5c5.4 0 10.3-2.1 14-5.4l-6.5-5.5c-2 1.4-4.6 2.3-7.5 2.3-5.2 0-9.6-3.3-11.2-8l-6.6 5.1C8.6 41 15.7 45.5 24 45.5z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.4l6.5 5.5c-.5.4 7-5.1 7-14.4 0-1.2-.1-2.3-.4-3.5z"/>
              </svg>
              Accedi con Google
            </Button>

            <div className="text-center">
              <Link to="/forgot-password" className="text-sm text-[#166534] hover:underline" data-testid="forgot-password-link">
                Password dimenticata?
              </Link>
            </div>
            <div className="text-center">
              <button type="button" data-testid="switch-to-register" onClick={() => { setMode("register"); setError(""); }} className="text-sm text-[#166534] hover:underline">
                Sei un tesserato? Registrati
              </button>
            </div>
          </form>
          )}
          </div>
          )}

          <div className="mt-8 border-t border-slate-200 pt-6">
            <p className="mb-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-400">Associazione Nazionale Guardie Ecozoofile</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Link to="/sito" data-testid="site-link" className="flex items-center justify-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:border-[#166534] hover:bg-emerald-50 hover:text-[#166534]">
                <Globe className="h-4 w-4" /> Sito ufficiale
              </Link>
              <a href="https://docs.google.com/forms/d/e/1FAIpQLSfqxEQwPbqKqLAUo4dWP9W__6rR3pbMzioOGLjpM0NcGKCIGQ/viewform" target="_blank" rel="noopener noreferrer" data-testid="join-link" className="flex items-center justify-center gap-2 rounded-md bg-[#D97706] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#b45309]">
                <UserPlus className="h-4 w-4" /> Iscriviti all'associazione
              </a>
            </div>
            <Link to="/convenzioni" data-testid="convenzioni-link" className="mt-3 flex items-center justify-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:border-[#166534] hover:bg-emerald-50 hover:text-[#166534]">
              <Handshake className="h-4 w-4" /> Convenzioni CRAL ANGE
            </Link>
            <button type="button" onClick={openPresentazione} data-testid="presentazione-corso-button" className="mt-3 flex w-full items-center justify-center gap-2 rounded-md bg-[#166534] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#14532d]">
              <Presentation className="h-4 w-4" /> Presentazione Corso di Formazione d'ingresso
            </button>
            {presWin && !pdf && (
              <button type="button" onClick={closePresentazione} data-testid="presentazione-close-mobile" className="mt-2 flex w-full items-center justify-center gap-2 text-xs text-slate-500 hover:text-red-700 lg:hidden">
                <X className="h-3 w-3" /> Chiudi presentazione
              </button>
            )}
            <div className="mt-3 text-center">
              <Link to="/dona" className="text-xs text-slate-400 hover:text-[#166534] hover:underline" data-testid="public-donation-link">
                Sostienici con una donazione →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
